import type I18nKit from './index'

/**
 * 事件枚举
 */
export enum I18nEventType {
  /** 切换语言 */
  LANGUAGE_CHANGED = 'language_changed',
}

/**
 * 支持的语言代码
 */
export type SupportedLocale = 'zh-CN' | 'en-US'

/**
 * 事件类型定义
 */
export interface I18nEvents {
  [I18nEventType.LANGUAGE_CHANGED]: {
    language: SupportedLocale
  }
}

// 定义元数据结构
export type Meta = {
  [key: string]: unknown
}

// 定义渲染器类型
export type Renderer = (value: unknown, meta?: Meta) => string | HTMLElement

/**
 * 环境标签， 值分别为dev、sit、sit1、pre、prod
 */
export enum EnvTag {
  /** 开发环境 */
  DEV = 'dev',
  /** sit环境 */
  SIT = 'sit',
  /** sit1环境 */
  SIT1 = 'sit1',
  /** 预发布环境 */
  PRE = 'pre',
  /** 生产环境 */
  PROD = 'prod',
}

/**
 * AuthKit 配置选项
 * @interface I18nConfig
 */
export interface I18nConfig {
  /** 环境标签，确定标签通信的作用域, 如dev|sit|pre... */
  envTag?: EnvTag | (() => EnvTag)
  /** Cookie 存储的顶级域名，默认自动获取 */
  cookieDomain?: string
  defaultLocale?: string
  fallbackLocale?: string
  backend?: {
    loadPath: string | ((languages: string[], namespaces: string[]) => string)
  }
  detection?: {
    order: string[]
  }
  /** 静态资源域名，用于跨域通信 */
  staticOrigin?: string
  // 其他可能的配置选项
  debug?: boolean
  resources?: Record<string, Record<string, unknown>>
  // 添加自定义渲染器配置
  renderers?: Record<string, Renderer>
  /** 语言切换回调 */
  onLanguageChange?: (language: SupportedLocale) => void
}

/**
 * I18nKit 的对外安全代理接口
 *
 * 在实例初始化完成前调用也能安全工作：
 * - `$t(key)` 返回 key 本身（fakeT 语义）
 * - `setLocale` 静默忽略
 * - `getCurrentLanguage` 返回默认值 `zh-CN`
 *
 * 这是两个 app 中手写 `i18n` 导出对象的统一形态。
 */
export interface I18nProxy {
  /** 翻译函数；实例未初始化时返回 key 本身 */
  $t: (key: string, data?: Record<string, unknown> | string, fallback?: string) => string
  /** 切换语言（触发整页 reload 或自定义回调） */
  setLocale: (locale: SupportedLocale) => void
  /** 获取当前语言；实例未初始化时返回 `zh-CN` */
  getCurrentLanguage: () => string
}

/**
 * createI18n 工厂配置
 */
export interface CreateI18nOptions {
  /**
   * 环境标签（决定 cookie 作用域与多页签同步 channel）
   *
   * 省略时自动取 `@peerup/host-env` 的 `env`（即 `window._envsConfig.VITE_APP_ENV`，默认 `'dev'`）。
   * SDK / 嵌入式等 `host-env` 未注入的场景可显式传入以获得完全控制。
   */
  envTag?: EnvTag
  /** locales 目录的 URL 前缀。web: `''`；accounts: `/accounts` */
  localeBasePath?: string
  /** 版本号，用于 manifest cache-busting */
  version?: string
  /** 项目类型，决定 API 词条来源，如 `peerup` / `accounts` */
  projectType?: string
  /** 自定义 API 词条地址；不传则用默认模板拼接 */
  apiLocaleUrl?: string
  /** 是否加载 API 词条；默认在 dev/sit/sit1/pre 环境开启 */
  enableApiLocale?: boolean | ((env: string) => boolean)
  /** 静态资源域名，用于跨域通信 */
  staticOrigin?: string
  /** 自定义渲染器 */
  renderers?: Record<string, Renderer>
  /** 回退语言，默认 `zh-CN` */
  fallbackLocale?: SupportedLocale
  /** 语言切换回调 */
  onLanguageChange?: (locale: SupportedLocale) => void
}

/**
 * createI18n 返回值
 */
export interface I18nHandle {
  /** 已初始化完成的 I18nKit 实例 */
  instance: I18nKit
  /** 实际使用的 locale */
  locale: SupportedLocale
  /** 安全代理对象（未初始化时也能安全调用） */
  proxy: I18nProxy
}
